import { z } from "zod";
import { decodeFunctionData, decodeFunctionResult, encodeFunctionData, type Hex } from "viem";
import { ponsRewardNftAbi } from "../contracts/pons-reward-nft";
import { currentChainId, type Eip1193Provider, type WalletTransactionReceipt } from "./eip1193";
import { readNftState } from "./nft-lifecycle";
import { readRpc, type RpcReadMethod } from "./read-rpc";
import type { TrackedTransaction } from "./transaction-tracker";

const hex = z.custom<Hex>(value => typeof value === "string" && /^0x(?:[0-9a-f]{2})*$/i.test(value));
const hash = z.custom<Hex>(value => typeof value === "string" && /^0x[0-9a-f]{64}$/i.test(value));
const address = z.custom<Hex>(value => typeof value === "string" && /^0x[0-9a-f]{40}$/i.test(value));
const quantity = z.string().regex(/^0x(?:0|[1-9a-f][0-9a-f]*)$/i);
const blockSchema = z.object({ number: quantity, hash, timestamp: quantity });
const receiptSchema = z.object({ transactionHash: hash, blockNumber: quantity, blockHash: hash,
  status: z.enum(["0x0", "0x1"]), logs: z.array(z.object({ address, data: hex, topics: z.array(hash) })) });
const transactionSchema = z.object({ hash, from: address, to: address, input: hex, value: quantity });
const same = (a: string, b: string) => a.toLowerCase() === b.toLowerCase();

/** Read-only reconciliation. Missing receipts alone never make an old send safe to retry. */
export async function reconcileRedemption(provider: Eip1193Provider, item: TrackedTransaction,
  signal: AbortSignal): Promise<WalletTransactionReceipt | null> {
  if (!item.hash || item.tokenId === null) throw new Error("Redemption identity is unavailable");
  const options = { signal };
  const guard = () => signal.throwIfAborted();
  const request = async (method: RpcReadMethod, params: readonly unknown[]) => {
    guard();
    const result = await readRpc(provider, { method, params }, options);
    guard();
    return result;
  };
  const checkChain = async () => {
    if (await currentChainId(provider, options) !== item.chainId) throw new Error(`WRONG_CHAIN:${item.chainId}`);
    guard();
  };
  await checkChain();
  const pin = blockSchema.parse(await request("eth_getBlockByNumber", ["latest", false]));
  if (item.receiptBlockNumber && BigInt(pin.number) < BigInt(item.receiptBlockNumber)) {
    throw new Error("RPC is behind the previously observed receipt");
  }
  const checkPin = async () => {
    const current = blockSchema.parse(await request("eth_getBlockByNumber", [pin.number, false]));
    if (!same(current.hash, pin.hash) || current.number !== pin.number || current.timestamp !== pin.timestamp) {
      throw new Error("Reconciliation block changed");
    }
    await checkChain();
  };
  const canonicalReceipt = async (): Promise<WalletTransactionReceipt | null> => {
    const raw = await request("eth_getTransactionReceipt", [item.hash]);
    if (raw === null) return null;
    const receipt = receiptSchema.parse(raw);
    if (!same(receipt.transactionHash, item.hash!)) throw new Error("Receipt transaction mismatch");
    if (BigInt(receipt.blockNumber) > BigInt(pin.number)) throw new Error("A newer receipt requires another check");
    const block = blockSchema.parse(await request("eth_getBlockByNumber", [receipt.blockNumber, false]));
    if (block.number !== receipt.blockNumber) throw new Error("Receipt block number mismatch");
    return same(block.hash, receipt.blockHash) ? receipt : null;
  };
  const receipt = await canonicalReceipt();
  if (receipt) { await checkPin(); return receipt; }

  // Older records lack a saved deadline. Recover it only from the exact transaction,
  // not from the browser clock, an API estimate or a newly prepared redemption.
  const savedDeadline = item.redeemSubmittedDeadline ?? item.redeemDeadline;
  let deadline = savedDeadline === undefined ? null : BigInt(savedDeadline);
  if (deadline === null) {
    const transaction = transactionSchema.parse(await request("eth_getTransactionByHash", [item.hash]));
    const call = decodeFunctionData({ abi: ponsRewardNftAbi, data: transaction.input });
    if (!same(transaction.hash, item.hash) || !same(transaction.from, item.account) || !same(transaction.to, item.collection) ||
      BigInt(transaction.value) !== 0n || (call.functionName !== "redeem" && call.functionName !== "redeemWithFees") ||
      call.args[0].toString() !== item.tokenId ||
      (item.redeemMinimumWei !== undefined && call.args[1].toString() !== item.redeemMinimumWei)) {
      throw new Error("Cannot recover the original redemption deadline");
    }
    deadline = call.args[2];
  }
  if (BigInt(pin.timestamp) < deadline) throw new Error("The previous redemption can still be included; check again after its deadline");

  const state = await readNftState(provider, { chainId: item.chainId, collection: item.collection as Hex,
    tokenId: BigInt(item.tokenId), block: { number: pin.number, hash: pin.hash } }, guard, options);
  const owner = decodeFunctionResult({ abi: ponsRewardNftAbi, functionName: "ownerOf", data: hex.parse(await request("eth_call", [{
    to: item.collection, data: encodeFunctionData({ abi: ponsRewardNftAbi, functionName: "ownerOf", args: [BigInt(item.tokenId)] }),
  }, pin.number])) });
  if (state.status !== 3 || !same(state.sourceToken, item.sourceToken) || !same(owner, item.account)) {
    throw new Error("The restored NFT owner, source or finalized state is unverified");
  }
  // A receipt may reappear while the state reads are in flight. Reverify it rather
  // than clearing its blocker using the earlier null response.
  const latestReceipt = await canonicalReceipt();
  await checkPin();
  return latestReceipt;
}
